import { getContactSelectQueryKey } from '@/components/Form/Shared/ContactSelect'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { deleteContact, getAccountStatus } from '@/modules/Customer/services'
import { ModalProps } from '@/types/shared'
import { useQueryClient } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog, GridHookForm, Typogaphy } from 'vnetwork-ui'

export interface DeleteContactModalProps extends ModalProps {
    customer_id: number
    contact_id: number
    contact_name: string
}

type AuthAction = 'keep' | 'unlink' | 'delete'

interface DeleteContactFormValues {
    message: string
    auth_action: AuthAction
}

const AUTH_ACTION_OPTIONS = (t: (key: string) => string) => [
    {
        label: t('auth-action-keep'),
        value: 'keep' as AuthAction,
    },
    {
        label: t('auth-action-unlink'),
        value: 'unlink' as AuthAction,
    },
    {
        label: t('auth-action-delete'),
        value: 'delete' as AuthAction,
    },
]

const DeleteContactModal: React.FC<DeleteContactModalProps> = ({
    onClose,
    open,
    customer_id,
    contact_id,
    contact_name,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    // Check if contact has authentication account
    const { data: accountStatus, isLoading: isLoadingStatus } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.ACCOUNT_STATUS, contact_id],
                queryFn: () => getAccountStatus(contact_id),
                enabled: !!contact_id,
            },
            { showLoading: false, showError: false }
        )

    const hasAuthAccount = accountStatus?.has_account ?? false
    const authMethod = accountStatus?.authentication_method

    const formControl = useForm<DeleteContactFormValues>({
        defaultValues: {
            message: '',
            auth_action: 'keep',
        },
    })

    const authAction = formControl.watch('auth_action')

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: deleteContact,
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_CONTACTS,
                QUERY_KEYS.ACCOUNT_STATUS,
            ],
            successMessage: t('delete-contact-success'),
            errorMessage: t('delete-contact-error'),
            onSuccess: () => {
                // Invalidate contact select dropdown
                queryClient.invalidateQueries({
                    queryKey: getContactSelectQueryKey(customer_id),
                })
                onClose()
            },
        }
    )

    const handleDelete = (values: DeleteContactFormValues) => {
        mutateAsync({
            customer_id,
            contact_id,
            message: values?.message,
            auth_action: hasAuthAccount ? values.auth_action : undefined,
        })
    }

    return (
        <Dialog onClose={onClose} open={open} title={t('delete-contact')}>
            <Alert variant="error" className="mb-4">
                {t('delete-contact-warning', { name: contact_name })}
            </Alert>

            {/* Show auth account info if exists */}
            {!isLoadingStatus && hasAuthAccount && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="h-5 w-5 text-orange-600 flex-shrink-0" />
                        <Typogaphy className="font-medium text-orange-800">
                            {t('contact-has-auth-account')} (
                            {authMethod === 'keycloak'
                                ? 'Keycloak SSO'
                                : authMethod}
                            )
                        </Typogaphy>
                    </div>
                </div>
            )}

            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('delete'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                data={[
                    {
                        className: 'col-span-4',
                        formItemProps: {
                            name: 'message',
                            type: 'textarea',
                            placeholder: t('delete-contact-reason-placeholder'),
                            label: t('delete-contact-reason'),
                        },
                    },
                    // Only show auth action if contact has auth account
                    ...(hasAuthAccount
                        ? [
                              {
                                  className: 'col-span-4',
                                  formItemProps: {
                                      name: 'auth_action' as const,
                                      type: 'radiogroup' as const,
                                      label: t('auth-account-action'),
                                      options: AUTH_ACTION_OPTIONS(t),
                                  },
                              },
                          ]
                        : []),
                    // Show warning for delete action
                    ...(hasAuthAccount && authAction === 'delete'
                        ? [
                              {
                                  className: 'col-span-4',
                                  renderEditComponent: () => (
                                      <Alert variant="warning">
                                          {t('auth-action-delete-warning')}
                                      </Alert>
                                  ),
                              },
                          ]
                        : []),
                ]}
                onSubmit={handleDelete}
            />
        </Dialog>
    )
}

export default DeleteContactModal
